// Roles holding a delete grant on property_searches that a CRM session can
// carry. The grant is row-filtered for agent, so a refusal comes back as zero
// rows, not an error: callers treat `affected_rows === 0` as a failure. Keyed
// on the session role, not `me.is_admin` (auth derives the role from it).
const ROLES_ALLOWED_TO_DELETE = new Set(['agent', 'tenant_admin']);

export const canDeletePropertySearch = (
  hasuraRole: string | null | undefined,
): boolean => hasuraRole != null && ROLES_ALLOWED_TO_DELETE.has(hasuraRole);

type PropertySearchDeleteScope = {
  source?: string | null;
  created_by?: string | null;
  user?: { teams_users: ReadonlyArray<{ team_id: string }> } | null;
};

// Per-row check for agent: created by me, or a `crm` search whose contact is on
// one of my active teams (`myTeamIds`). `enquiry` searches stay offered because
// the CRM cannot read the lots the server checks; other sources are hidden.
// The agent `teams_users` select hides deactivated rows except in teams I lead,
// and the server's crm arm counts active ones only. Neither that nor the
// shared-enquiry guard is replayed, so it can over-offer and the server
// decides. Mirrors the agent delete filter.
export const canDeletePropertySearchRow = ({
  hasuraRole,
  meId,
  myTeamIds,
  propertySearch,
}: {
  hasuraRole: string | null | undefined;
  meId: string;
  myTeamIds: ReadonlySet<string>;
  propertySearch: PropertySearchDeleteScope;
}): boolean => {
  if (!canDeletePropertySearch(hasuraRole)) {
    return false;
  }
  if (hasuraRole !== 'agent') {
    return true;
  }
  if (propertySearch.created_by === meId) {
    return true;
  }
  switch (propertySearch.source) {
    case 'enquiry':
      return true;
    case 'crm':
      return (
        propertySearch.user?.teams_users.some(({ team_id }) =>
          myTeamIds.has(team_id),
        ) ?? false
      );
    default:
      return false;
  }
};

// Postgres names (the second is truncated at 63 characters).
const CHILD_PRIMARY_KEYS = [
  '"property_searches_places_pkey"',
  '"property_searches_property_types_property_search_id_property_ty"',
];

// A save the agent filter refuses, when it carries places or types: their
// insert check fails, or, when a kept place or type is re-inserted, the
// refused delete leaves its row and the primary key fails first. An allowed
// save deletes them first in the same transaction, so that clash means a
// refusal too, barring two saves of the same search at once (rolled back,
// reported as refused).
export const isRefusedSaveError = (
  errors: ReadonlyArray<{
    message: string;
    extensions?: Record<string, unknown>;
  }>,
): boolean =>
  errors.some(
    ({ message, extensions }) =>
      extensions?.code === 'permission-error' ||
      (extensions?.code === 'constraint-violation' &&
        CHILD_PRIMARY_KEYS.some(key => message.includes(key))),
  );
